import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Brain, TrendingUp, Users, AlertCircle, Sparkles,
} from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { isUserPartner } from '../../lib/auth';
import { getPartnerIdForUser, loadTeamResults, computeTeamAverages } from '../../lib/teams';
import {
  computeFullTeamMetrics,
  loadInsightSnapshots,
  saveInsightSnapshot,
  metricDefinitions,
  getMetricLevel,
} from '../../lib/team-insights';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import type { TeamMetrics, InsightSnapshot, MetricDefinition } from '../../lib/team-insights';

function MetricCard({ def, value, previousValue }: {
  def: MetricDefinition;
  value: number;
  previousValue?: number;
}) {
  const level = getMetricLevel(value, def.higherIsBetter);
  const delta = previousValue !== undefined ? value - previousValue : undefined;
  const improving = delta !== undefined && (def.higherIsBetter ? delta > 0 : delta < 0);
  const declining = delta !== undefined && (def.higherIsBetter ? delta < 0 : delta > 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-sand-200 p-5 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-3">
        <h4 className="text-sm font-semibold text-chocolate-800">{def.label}</h4>
        {delta !== undefined && delta !== 0 && (
          <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
            improving ? 'bg-emerald-50 text-emerald-700' : declining ? 'bg-red-50 text-red-700' : 'bg-sand-100 text-sand-600'
          }`}>
            {delta > 0 ? '+' : ''}{delta}
          </span>
        )}
      </div>
      <div className="flex items-end gap-3">
        <span className="text-3xl font-bold text-chocolate-900">{value}</span>
        <span className={`text-sm font-medium mb-1 ${level.color}`}>{level.label}</span>
      </div>
      <div className="mt-3 w-full bg-sand-100 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all duration-700 ${
            def.higherIsBetter
              ? value > 75 ? 'bg-emerald-500' : value > 55 ? 'bg-emerald-400' : value > 30 ? 'bg-amber-400' : 'bg-red-400'
              : value <= 20 ? 'bg-emerald-500' : value <= 40 ? 'bg-amber-400' : value <= 60 ? 'bg-orange-400' : 'bg-red-500'
          }`}
          style={{ width: `${Math.min(100, value)}%` }}
        />
      </div>
      <p className="text-xs text-sand-600 mt-2">{def.description}</p>
    </motion.div>
  );
}

function TrendChart({ snapshots, metricKey, label, higherIsBetter }: {
  snapshots: InsightSnapshot[];
  metricKey: keyof TeamMetrics;
  label: string;
  higherIsBetter: boolean;
}) {
  if (snapshots.length < 2) return null;

  const values = snapshots.map(s => s.metrics[metricKey] as number);
  const max = Math.max(...values, 100);
  const min = Math.min(...values, 0);
  const range = max - min || 1;

  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * 100;
    const y = 100 - ((v - min) / range) * 80 - 10;
    return `${x},${y}`;
  });

  const latest = values[values.length - 1];
  const first = values[0];
  const trend = latest - first;
  const improving = higherIsBetter ? trend > 0 : trend < 0;

  return (
    <div className="bg-white rounded-xl border border-sand-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-chocolate-700">{label}</span>
        <span className={`text-xs font-medium ${improving ? 'text-emerald-600' : trend === 0 ? 'text-sand-500' : 'text-red-600'}`}>
          {trend > 0 ? '+' : ''}{trend} overall
        </span>
      </div>
      <svg viewBox="0 0 100 100" className="w-full h-16" preserveAspectRatio="none">
        <polyline
          points={points.join(' ')}
          fill="none"
          stroke={improving ? '#059669' : trend === 0 ? '#9ca3af' : '#dc2626'}
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
        {values.map((v, i) => {
          const x = (i / (values.length - 1)) * 100;
          const y = 100 - ((v - min) / range) * 80 - 10;
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r="2"
              fill={improving ? '#059669' : trend === 0 ? '#9ca3af' : '#dc2626'}
            />
          );
        })}
      </svg>
      <div className="flex justify-between text-[10px] text-sand-500 mt-1">
        <span>{snapshots[0].snapshotDate}</span>
        <span>{snapshots[snapshots.length - 1].snapshotDate}</span>
      </div>
    </div>
  );
}

export function TeamInsights() {
  const navigate = useNavigate();
  const { teamId } = useParams<{ teamId: string }>();
  const { user, isLoading: authLoading } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [teamName, setTeamName] = useState('');
  const [metrics, setMetrics] = useState<TeamMetrics | null>(null);
  const [snapshots, setSnapshots] = useState<InsightSnapshot[]>([]);
  const [previousMetrics, setPreviousMetrics] = useState<TeamMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<'capability' | 'harmonic' | 'distribution'>('capability');

  useEffect(() => {
    if (authLoading) return;
    if (!user || !teamId) {
      navigate('/login');
      return;
    }

    const init = async () => {
      try {
        const isPartner = await isUserPartner(user.id);
        if (!isPartner) { navigate('/login'); return; }

        const pId = await getPartnerIdForUser(user.id);
        if (!pId) { navigate('/login'); return; }

        const { data: team } = await supabase
          .from('teams')
          .select('name, partner_id')
          .eq('id', teamId)
          .maybeSingle();

        if (!team || team.partner_id !== pId) {
          navigate('/partner/teams');
          return;
        }

        setTeamName(team.name);

        const [resultsData, loadedSnapshots] = await Promise.all([
          loadTeamResults(teamId),
          loadInsightSnapshots(teamId),
        ]);

        setSnapshots(loadedSnapshots);

        if (loadedSnapshots.length > 0) {
          setPreviousMetrics(loadedSnapshots[loadedSnapshots.length - 1].metrics);
        }

        const averages = computeTeamAverages(teamId, team.name, resultsData);
        const computed = computeFullTeamMetrics(averages, resultsData);
        setMetrics(computed);

        // Save a snapshot for today
        if (resultsData.length > 0) {
          await saveInsightSnapshot(
            teamId,
            averages.memberCount,
            averages.membersWithResults,
            computed
          );
        }
      } catch (err: any) {
        console.error('Error loading team insights:', err);
        setError(err.message || 'Failed to load team insights');
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate, teamId]);

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sand-50 to-white flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sand-50 to-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-chocolate-600">{error}</p>
        </div>
      </div>
    );
  }

  const categoryDefs = metricDefinitions.filter(d => d.category === activeCategory);

  const categories = [
    { key: 'capability' as const, label: 'Capabilities', icon: Brain, count: 6 },
    { key: 'harmonic' as const, label: 'Emotional Intelligence', icon: Sparkles, count: 5 },
    { key: 'distribution' as const, label: 'Team Distribution', icon: Users, count: 2 },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-sand-50 to-white">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link
            to={`/partner/teams/${teamId}`}
            className="p-2 rounded-lg hover:bg-sand-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-chocolate-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-chocolate-900">Team Insights</h1>
            <p className="text-sand-600">{teamName}</p>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {categories.map(cat => (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeCategory === cat.key
                  ? 'bg-chocolate-800 text-white'
                  : 'bg-white text-chocolate-600 border border-sand-200 hover:bg-sand-50'
              }`}
            >
              <cat.icon className="w-4 h-4" />
              {cat.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                activeCategory === cat.key ? 'bg-white/20' : 'bg-sand-100'
              }`}>
                {cat.count}
              </span>
            </button>
          ))}
        </div>

        {/* Metrics Grid */}
        {metrics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
            {categoryDefs.map((def, i) => (
              <motion.div key={def.key} transition={{ delay: i * 0.05 }}>
                <MetricCard
                  def={def}
                  value={metrics[def.key]}
                  previousValue={previousMetrics?.[def.key]}
                />
              </motion.div>
            ))}
          </div>
        )}

        {/* Trend Charts */}
        {snapshots.length >= 2 && (
          <div className="mt-10">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-chocolate-600" />
              <h2 className="text-lg font-semibold text-chocolate-800">Trends Over Time</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categoryDefs.map(def => (
                <TrendChart
                  key={def.key}
                  snapshots={snapshots}
                  metricKey={def.key}
                  label={def.label}
                  higherIsBetter={def.higherIsBetter}
                />
              ))}
            </div>
          </div>
        )}

        {snapshots.length < 2 && (
          <div className="mt-8 bg-sand-50 border border-sand-200 rounded-xl p-6 text-center">
            <TrendingUp className="w-8 h-8 text-sand-400 mx-auto mb-2" />
            <p className="text-sm text-sand-600">
              Trend data will appear here as members complete assessments over time.
              Each visit saves a snapshot for comparison.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
